import { useCallback, useEffect, useMemo, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import useGlobalFilters from '../../../../hooks/useGlobalFilters';
import useDebouncedValue from '../../../../hooks/useDebouncedValue';
import { TranslateFunction } from '../../../../hooks/useTranslate';
import useDownloadFile from '../../../../hooks/useDownloadFile';
import {
    useDescargarArchivoSiniestroMutation,
    useExportSiniestrosGestionSrtMutation,
    useExportSrtFormularioArchivoMutation,
    useGetSiniestrosQuery
} from '../../../../services/roamApi';
import { getCurrentDate, getDateTextFieldFormat } from '../../../../utils/common';
import {
    useDescargarPdfInvestigacionSiniestroMutation,
    useDownloadVisitaActaMutation,
    useLazyGetVisitaByIdQuery
} from '../../../../services/visitasApi';
import { CommonMimeType, downloadBase64File } from '../../../../utils/base64FileDownload';
import { buildActaInterviewedData } from '../../../Visitas/utils/actaPayload';
import { mapRoamSiniestro } from '../../../Siniestros/adapters/roam';
import { SrtStatusEnum } from '../../../Siniestros/components/SiniestrosFilters/constants';
import { FiltersState, SelectOption } from '../../../Siniestros/components/SiniestrosFilters/types';
import { SiniestroItem, SiniestroStatus } from '../../../Siniestros/types/siniestros.types';
import { descargarDesdeUrl } from '../../../Siniestros/utils/descargarDesdeUrl';
import { getVisitaPresentada } from '../../../Siniestros/utils/visitaPresentada';
import {
    buildSiniestrosQueryParams,
    TAB_STATUS_MAP
} from '../../../Siniestros/utils/siniestrosQueryParams';

// La tab solo lista siniestros con el formulario SRT listo para exportar (SMP-561)
export const ESTADOS_SRT_EXPORTABLES = [SrtStatusEnum.CARGADO, SrtStatusEnum.EXPORTADO];

export const SRT_STATUS_OPTIONS_EXPORTABLES: SelectOption[] = [
    { id: SrtStatusEnum.ALL, label: 'Todos' },
    // Se nombran desde la generación del archivo: Cargado está pendiente y Exportado ya se generó
    { id: SrtStatusEnum.CARGADO, label: 'PENDIENTE' },
    { id: SrtStatusEnum.EXPORTADO, label: 'GENERADO' }
];

// Cargado o Exportado ya tienen formulario SRT: alcanza con el accidente laboral (P3)
export const isSeleccionableArchivosSrt = (siniestro: SiniestroItem): boolean =>
    !!siniestro.idAccidenteLaboral;

interface UseFileGenerationSiniestrosParams {
    enabled: boolean;
    translate: TranslateFunction;
}

const useFileGenerationSiniestros = ({ enabled, translate }: UseFileGenerationSiniestrosParams) => {
    const {
        filters,
        setFilters,
        extraFilters,
        handleSubmitSearch,
        clearFilters,
        handleChangeExtraFilters
    } = useGlobalFilters({ ordenador: 'fechaDenuncia:desc' });

    // El cliente es el primer filtro y aplica al momento, sin esperar a Buscar (P2)
    const [clientId, setClientId] = useState<number | null>(null);
    const [activeTab, setActiveTab] = useState<SiniestroStatus>('ALL');
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

    const [exportSrtFormularioArchivo, { isLoading: isGenerandoArchivo }] =
        useExportSrtFormularioArchivoMutation();
    const [descargarArchivoSiniestro, { isLoading: isDescargandoTxt }] =
        useDescargarArchivoSiniestroMutation();
    const [descargarPdfInvestigacion, { isLoading: isDescargandoInforme }] =
        useDescargarPdfInvestigacionSiniestroMutation();
    const [getVisitaById, { isFetching: isBuscandoVisita }] = useLazyGetVisitaByIdQuery();
    const [downloadVisitaActa, { isLoading: isDescargandoActa }] = useDownloadVisitaActaMutation();
    const [exportSiniestrosGestionSrt, { isLoading: isGenerandoExcel }] =
        useExportSiniestrosGestionSrtMutation();
    const { downloadFile, isDownloading: isDescargandoExcel } = useDownloadFile();

    const filtrosConCliente: FiltersState = useMemo(
        () => ({ ...(extraFilters || {}), clientId: clientId ?? undefined }),
        [extraFilters, clientId]
    );

    const empleadoDebounced = useDebouncedValue(extraFilters?.employee, 400);
    const nroRoamDebounced = useDebouncedValue(extraFilters?.nroRoam, 400);

    const queryParams = useMemo(
        () => ({
            ...buildSiniestrosQueryParams({
                filters,
                extraFilters: filtrosConCliente,
                idsEstado: TAB_STATUS_MAP[activeTab],
                idPreventor: filtrosConCliente.idPreventor,
                empleado: empleadoDebounced,
                nroRoam: nroRoamDebounced
            }),
            // "Todos" del filtro Estado SRT son Cargado y Exportado (P4)
            idEstadoSRT: undefined,
            idsEstadoSRT: filtrosConCliente.srtStatus
                ? [filtrosConCliente.srtStatus]
                : ESTADOS_SRT_EXPORTABLES
        }),
        [filters, filtrosConCliente, activeTab, empleadoDebounced, nroRoamDebounced]
    );

    const { data, isLoading, isFetching, refetch } = useGetSiniestrosQuery(queryParams, {
        skip: !enabled
    });

    const siniestros = useMemo(() => (data?.data || []).map(mapRoamSiniestro), [data]);

    useEffect(() => {
        setSelectedIds(new Set());
    }, [clientId, filters?.skip]);

    const seleccionEstado = useMemo((): 'none' | 'some' | 'all' => {
        const habilitados = siniestros.filter(isSeleccionableArchivosSrt);
        if (habilitados.length === 0 || selectedIds.size === 0) return 'none';
        const seleccionados = habilitados.filter((s) => selectedIds.has(Number(s.id)));
        if (seleccionados.length === habilitados.length) return 'all';
        if (seleccionados.length > 0) return 'some';
        return 'none';
    }, [siniestros, selectedIds]);

    const avisarFaltaClienteOSeleccion = useCallback(() => {
        enqueueSnackbar(translate('siniestros.enqueues.noClientOrSelection'), {
            variant: 'warning'
        });
    }, [translate]);

    const handleCambiarCliente = useCallback(
        (id: number | null) => {
            setClientId(id);
            // Contrato, organismo y establecimiento dependen del cliente
            handleChangeExtraFilters({ target: { name: 'idOrganismoCentral', value: null } });
            handleChangeExtraFilters({ target: { name: 'organismoId', value: null } });
            handleChangeExtraFilters({ target: { name: 'establishmentName', value: null } });
            setFilters((prevFilters: any) => ({ ...prevFilters, skip: 0 }));
        },
        [handleChangeExtraFilters, setFilters]
    );

    const handleToggleSeleccion = useCallback(
        (id: number, siniestro: SiniestroItem) => {
            if (!clientId) {
                avisarFaltaClienteOSeleccion();
                return;
            }
            if (!isSeleccionableArchivosSrt(siniestro)) return;
            setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) {
                    next.delete(id);
                } else {
                    next.add(id);
                }
                return next;
            });
        },
        [clientId, avisarFaltaClienteOSeleccion]
    );

    const handleSelectAll = useCallback(() => {
        if (!clientId) {
            avisarFaltaClienteOSeleccion();
            return;
        }
        setSelectedIds(
            new Set(siniestros.filter(isSeleccionableArchivosSrt).map((s) => Number(s.id)))
        );
    }, [clientId, siniestros, avisarFaltaClienteOSeleccion]);

    const handleClearSeleccion = useCallback(() => {
        setSelectedIds(new Set());
    }, []);

    // ZIP con un archivo por tipo que consolida los seleccionados; los pasa a Exportado (6e)
    const handleGenerarArchivo = useCallback(async () => {
        const idsAccidenteLaboral = siniestros
            .filter((s) => selectedIds.has(Number(s.id)) && s.idAccidenteLaboral)
            .map((s) => s.idAccidenteLaboral as number);

        if (!clientId || idsAccidenteLaboral.length === 0) {
            avisarFaltaClienteOSeleccion();
            return;
        }

        try {
            const response = await exportSrtFormularioArchivo({ idsAccidenteLaboral }).unwrap();
            if (!response?.presignedUrl) throw new Error('Missing presignedUrl');

            const fecha = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            await descargarDesdeUrl(response.presignedUrl, `SRT_export_${clientId}_${fecha}.zip`);
            setSelectedIds(new Set());
            refetch();
        } catch (error: any) {
            enqueueSnackbar(
                error?.data?.message || translate('siniestros.enqueues.generateError'),
                {
                    variant: 'error'
                }
            );
        }
    }, [
        siniestros,
        selectedIds,
        clientId,
        exportSrtFormularioArchivo,
        refetch,
        translate,
        avisarFaltaClienteOSeleccion
    ]);

    const handleDescargarTxt = useCallback(
        async (siniestro: SiniestroItem) => {
            try {
                const response = await descargarArchivoSiniestro(Number(siniestro.id)).unwrap();
                if (!response?.presignedUrl) throw new Error('Missing presignedUrl');

                await descargarDesdeUrl(response.presignedUrl, response.nombre);
                enqueueSnackbar(
                    translate('siniestros.enqueues.downloadSuccess', {
                        nroRoam: siniestro.nroRoam || siniestro.id
                    }),
                    { variant: 'success' }
                );
            } catch (error: any) {
                enqueueSnackbar(
                    error?.data?.message || translate('siniestros.enqueues.downloadError'),
                    { variant: 'error' }
                );
            }
        },
        [descargarArchivoSiniestro, translate]
    );

    // Mismo PDF que la tab SRT del detalle: Cargado o Exportado ya tienen el formulario completo
    const handleDescargarInforme = useCallback(
        async (siniestro: SiniestroItem) => {
            try {
                const pdfBase64 = await descargarPdfInvestigacion(Number(siniestro.id)).unwrap();
                if (!pdfBase64) throw new Error('PDF sin contenido');

                downloadBase64File({
                    base64Data: pdfBase64,
                    fileName: `investigacion-${siniestro.id}.pdf`,
                    mimeType: CommonMimeType.PDF
                });
            } catch {
                enqueueSnackbar(translate('siniestros.enqueues.downloadInformeError'), {
                    variant: 'error'
                });
            }
        },
        [descargarPdfInvestigacion, translate]
    );

    // El acta de siniestro lleva los datos del entrevistado, que vienen en la visita completa
    const handleDescargarActa = useCallback(
        async (siniestro: SiniestroItem) => {
            const visitaPresentada = getVisitaPresentada(siniestro);
            if (!visitaPresentada) return;

            try {
                const { data: visita } = await getVisitaById({
                    idVisita: Number(visitaPresentada.id)
                }).unwrap();

                const response = await downloadVisitaActa({
                    idVisita: visita.id,
                    idTipoVisita: visita.idTipoVisita ?? visita.actividades?.[0]?.tipoVisita?.id,
                    tipoActa: 1,
                    datos: buildActaInterviewedData(visita)
                }).unwrap();
                if (!response?.data) throw new Error('Acta sin contenido');

                downloadBase64File({
                    base64Data: response.data,
                    fileName: `acta-visita-${siniestro.nroRoam || siniestro.id}.pdf`,
                    mimeType: CommonMimeType.PDF
                });
            } catch {
                enqueueSnackbar(translate('siniestros.enqueues.downloadActaError'), {
                    variant: 'error'
                });
            }
        },
        [getVisitaById, downloadVisitaActa, translate]
    );

    // Excel con los mismos filtros de la grilla, sin paginado (SMP-625). Como en RGRL y
    // Visitas, el back lo sube a S3 y se descarga con fetch+blob desde la URL prefirmada.
    const handleExportExcel = useCallback(async () => {
        const { skip, take, ...filtrosExportacion } = queryParams;

        try {
            const response = await exportSiniestrosGestionSrt(filtrosExportacion).unwrap();
            const presignedUrl = response?.data?.presignedUrl;
            if (!presignedUrl) throw new Error('La respuesta no trae presignedUrl');

            const formatDate = getDateTextFieldFormat(getCurrentDate());
            const fileName = (
                response.data.nombreArchivo || `GestionSRT_Siniestros_${formatDate}`
            ).replace(/\.xlsx$/, '');

            const descargado = await downloadFile(presignedUrl, '.xlsx', fileName);
            if (!descargado) throw new Error('No se pudo descargar el Excel');
        } catch {
            enqueueSnackbar(translate('alerts.export.error'), { variant: 'error' });
        }
    }, [queryParams, exportSiniestrosGestionSrt, downloadFile, translate]);

    // Limpiar del toolbar y cambiar de tab resetean todo, cliente incluido
    const reset = useCallback(() => {
        clearFilters();
        setActiveTab('ALL');
        setClientId(null);
        setSelectedIds(new Set());
    }, [clearFilters]);

    return {
        filters,
        setFilters,
        extraFilters: filtrosConCliente,
        handleSubmitSearch,
        handleChangeExtraFilters,
        handleLimpiarFiltros: reset,
        reset,
        clientId,
        handleCambiarCliente,
        activeTab,
        setActiveTab,
        siniestros,
        isLoading: isLoading || isFetching,
        totalCount: data?.count ?? 0,
        refetch,
        selectedIds,
        seleccionEstado,
        handleToggleSeleccion,
        handleSelectAll,
        handleClearSeleccion,
        handleGenerarArchivo,
        isGenerandoArchivo,
        handleDescargarTxt,
        isDescargandoTxt,
        handleDescargarInforme,
        handleDescargarActa,
        isDescargandoPdf: isDescargandoInforme || isBuscandoVisita || isDescargandoActa,
        handleExportExcel,
        isExportandoExcel: isGenerandoExcel || isDescargandoExcel
    };
};

export default useFileGenerationSiniestros;
